/**
 * Date utilities for date range calculations
 */

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Get date range based on selected period
 */
export function getDateRangeFromPeriod(period: string): DateRange {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  let startDate: string;

  switch (period) {
    case 'today':
      startDate = endDate;
      break;
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = yesterday.toISOString().split('T')[0];
      break;
    }
    case 'last7Days': {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      startDate = weekAgo.toISOString().split('T')[0];
      break;
    }
    case 'last30Days': {
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 29);
      startDate = monthAgo.toISOString().split('T')[0];
      break;
    }
    case 'last3Months': {
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      startDate = threeMonthsAgo.toISOString().split('T')[0];
      break;
    }
    case 'last12Months': {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      startDate = yearAgo.toISOString().split('T')[0];
      break;
    }
    default: {
      // Default to last 7 days
      const defaultStart = new Date(today);
      defaultStart.setDate(defaultStart.getDate() - 6);
      startDate = defaultStart.toISOString().split('T')[0];
    }
  }

  return { startDate, endDate };
}

// Dashboard dimension type matching analyticsApi
export type DashboardDimensionValue = '7 days' | '30 days' | '3 months' | '6 months' | '1 year';

// Overview dimension type matching OverviewService
export type OverviewDimensionValue =
  | '12 months'
  | '12 weeks'
  | '4 weeks'
  | '7 days'
  | 'yesterday'
  | 'today';

/**
 * Convert period to Dashboard API dimension format
 */
export function getDashboardDimensionFromPeriod(period: string): DashboardDimensionValue {
  switch (period) {
    case 'today':
    case 'yesterday':
    case 'last7Days':
      return '7 days';
    case 'last30Days':
      return '30 days';
    case 'last3Months':
      return '3 months';
    case 'last12Months':
      return '1 year';
    default:
      return '7 days';
  }
}

/**
 * Convert period to Overview API dimension format
 */
export function getOverviewDimensionFromPeriod(period: string): OverviewDimensionValue {
  switch (period) {
    case 'today':
      return 'today';
    case 'yesterday':
      return 'yesterday';
    case 'last7Days':
      return '7 days';
    case 'last30Days':
      return '4 weeks';
    case 'last3Months':
      return '12 weeks';
    case 'last12Months':
      return '12 months';
    default:
      return '7 days';
  }
}

/**
 * Convert period to API dimension format (generic - for backwards compatibility)
 * @deprecated Use getDashboardDimensionFromPeriod or getOverviewDimensionFromPeriod instead
 */
export function getDimensionFromPeriod(period: string): OverviewDimensionValue {
  return getOverviewDimensionFromPeriod(period);
}

/**
 * Calculate time saved by AI based on conversation count
 * Formula: total conversations * 6 minutes (estimated manual handling time)
 * @param conversationCount - Total number of conversations
 * @returns Time saved formatted as "Xh" or "Xmin" depending on duration
 */
export function calculateTimeSavedByAI(conversationCount: number): string {
  const MINUTES_PER_CONVERSATION = 6;
  const totalMinutes = conversationCount * MINUTES_PER_CONVERSATION;

  // If less than 60 minutes, show minutes
  if (totalMinutes < 60) {
    return `${totalMinutes}min`;
  }

  // Otherwise show hours with one decimal place
  const totalHours = (totalMinutes / 60).toFixed(1);
  return `${totalHours}h`;
}

/**
 * Calculate time saved percentage change (mock for now, can be connected to API later)
 * @param currentCount - Current period conversation count
 * @param previousCount - Previous period conversation count
 * @returns Percentage change
 */
export function calculateTimeSavedChange(currentCount: number, previousCount: number): number {
  if (previousCount === 0) {
    return currentCount > 0 ? 100 : 0;
  }
  return Math.round(((currentCount - previousCount) / previousCount) * 100);
}
